import { createSlice } from "@reduxjs/toolkit"
import { apiCall } from "../../utils/middlewares/ApiActions";
import { queryKeyes } from "shared/queryKeys";
import { sortUniqueNumbers } from "GenericForms/Helper";

export interface Reporting {
    [key: string]: string | number | boolean | string[] | any,
}
export type VesselState = null |
    'VIEW' |
    'EDIT' |
    'NEW' |
    'ADDITIONAL_INFO' |
    'VESSEL_CONFIG' |
    'VOYAGE_REPORTING' |
    'VOYAGE_REPORTING_EDIT' |
    'VOYAGE_ANALYSIS' |
    'CREATE_VOYAGE_REPORTING' |
    'ONGOING_VESSEL_REPORTING' |
    'COMPLETED_VESSEL_REPORTING' |
    'CREATE_VESSEL_REPORTING' |
    'DO_REPORTING' |
    'ENGG_REPORTING' |
    'INITIAL_VESSEL_REPORTING' |
    'REPORTING_DATA_IMPORT' |
    'VIEW_IMPORTED_DATA' |
    'CONFIRM_ROB' |
    'EDIT_REPORTING' |
    'UPLOADED_FILES' |
    'OFF_HIRE';

export interface ReportingState {
    Vessels: Reporting[],
    onboardedVessels: Reporting[],
    loading: boolean,
    fileLoading: boolean,
    reportingLoading: boolean,
    reportingError: boolean,
    error: boolean,
    VesselID: number,
    VoyageID: number,
    ReportID: number,
    Reports: Reporting[],
    Voyages: Reporting[],
    VesselState: VesselState,
    ReportingImportFileID: number,
    LimitsAndAlerts: Reporting,
    CurrentVessel: Reporting,
    CurrentReport: Reporting,
    PreviousReport: Reporting,
    ConfigurationLimits: Reporting,
    CurrentReportFiles: Reporting,
    DraftID: number,
    SelectedOperations: any[],
    VesselMasterPageURL: string,
    alertsBuffer: any[],
}

const initialState: ReportingState = {
    Vessels: [],
    onboardedVessels: [],
    loading: false,
    fileLoading: false,
    reportingLoading: false,
    reportingError: false,
    error: false,
    VesselID: null,
    VoyageID: null,
    ReportID: null,
    Reports: [],
    Voyages: [],
    VesselState: null,
    ReportingImportFileID: null,
    LimitsAndAlerts: null,
    CurrentVessel: null,
    CurrentReport: null,
    PreviousReport: null,
    ConfigurationLimits: null,
    CurrentReportFiles: null,
    DraftID: null,
    SelectedOperations: [],
    VesselMasterPageURL: null,
    alertsBuffer: [],
}

const ReportingSlice = createSlice({
    name: "reporting",
    initialState,
    reducers: {
        apiRequested: (state) => {
            state.loading = true;
            state.error = false;
        },
        reportingAPIRequested: (state) => {
            state.reportingLoading = true;
            state.error = false;
        },
        fileApiRequested: (state) => {
            state.fileLoading = true;
            state.error = false;
        },
        apiRequestedFailed: (state) => {
            state.loading = false;
            state.error = true;
        },
        reportingAPIRequestedFailed: (state) => {
            state.reportingLoading = false;
            state.error = true;
        },
        setVesselState: (state, action) => {
            state.VesselState = action.payload
        },
        setVesselID: (state, action) => {
            state.VesselID = action.payload
        },
        setDraftID: (state, action) => {
            state.DraftID = action.payload
        },
        setVoyageID: (state, action) => {
            state.VoyageID = action.payload
        },
        setReportID: (state, action) => {
            state.ReportID = action.payload
        },
        getVessels: (state, action) => {
            state.Vessels = action.payload;
            state.loading = false;
        },
        getVoyages: (state, action) => {
            state.Voyages = action.payload;
            state.loading = false;
        },
        getReports: (state, action) => {
            state.Reports = action.payload;
            state.loading = false;
        },
        setData: (state, action) => {
            state.Vessels.push(action.payload);
            state.loading = false;
            state.VesselState = null;
        },
        setReportingImportFileID: (state, action) => {
            state.ReportingImportFileID = action.payload
        },
        setOnboardedVessels: (state, action) => {
            state.onboardedVessels = action.payload;
            state.loading = false;
        },
        setLimitsAndAlerts: (state, action) => {
            state.LimitsAndAlerts = action.payload;
            state.loading = false;
        },
        setCurrentVessel: (state, action) => {
            state.CurrentVessel = action.payload;
            state.loading = false;
            state.error = false;
        },
        setCurrentReport: (state, action) => {
            state.CurrentReport = action.payload;
            state.error = false;
            state.reportingLoading = false;
            state.reportingError = false;
        },
        setPreviousReport: (state, action) => {
            state.PreviousReport =
                Object.keys(action.payload).includes('data') ?
                    action.payload :
                    {
                        data: {
                            vessel_reporting_information: action.payload
                        }
                    };
            state.error = false;
            state.reportingLoading = false;
            state.reportingError = false;
        },
        setConfigurationLimits: (state, action) => {
            state.ConfigurationLimits = action.payload;
            state.loading = false;
            state.reportingLoading = false;
            state.reportingError = false;
        },
        setCurrentReportFiles: (state, action) => {
            state.CurrentReportFiles = action.payload;
            state.fileLoading = false;
        },
        setSelectedOperations: (state, action) => {
            state.SelectedOperations = sortUniqueNumbers(action.payload?.sort((a: any, b: any) => a - b));
        },
        setVesselMasterPageURL: (state, action) => {
            state.VesselMasterPageURL = action.payload;
        },
        addAlert: (state, action) => {
            state.alertsBuffer.push(action.payload);
        },
        clearAlerts: (state) => {
            state.alertsBuffer = [];
        },
    }
});

export const {
    apiRequested,
    reportingAPIRequested,
    fileApiRequested,
    apiRequestedFailed,
    reportingAPIRequestedFailed,
    setVesselState,
    setVesselID,
    setDraftID,
    setVoyageID,
    setReportID,
    getVessels,
    getVoyages,
    getReports,
    setReportingImportFileID,
    setOnboardedVessels,
    setLimitsAndAlerts,
    setCurrentReport,
    setPreviousReport,
    setCurrentVessel,
    setConfigurationLimits,
    setCurrentReportFiles,
    setSelectedOperations,
    setVesselMasterPageURL,
    addAlert,
    clearAlerts,
} = ReportingSlice.actions;
export default ReportingSlice.reducer;

// Action functions for generic forms
export const getVesselsAction = (url: string) => apiCall(
    {
        url,
        onStart: apiRequested.type,
        onSuccess: getVessels.type,
        onError: apiRequestedFailed.type,
    }
)

export const getVoyagesAction = (url: string) => apiCall(
    {
        url,
        onStart: apiRequested.type,
        onSuccess: getVoyages.type,
        onError: apiRequestedFailed.type,
    }
)

export const getReportsAction = (url: string) => apiCall(
    {
        url,
        onStart: apiRequested.type,
        onSuccess: getReports.type,
        onError: apiRequestedFailed.type,
    }
)

export const getLimitsAndAlertsAction = (url: string) => apiCall(
    {
        url,
        onStart: apiRequested.type,
        onSuccess: setLimitsAndAlerts.type,
        onError: apiRequestedFailed.type,
    }
)

export const getCurrentReportAction = (VesselID: number, ReportID: number) => apiCall(
    {
        url: queryKeyes.vessel.currentReport.url(VesselID, ReportID),
        onStart: reportingAPIRequested.type,
        onSuccess: setCurrentReport.type,
        onError: reportingAPIRequestedFailed.type,
    }
)

export const getPreviousReportAction = (VesselID: number, ReportID: number, lastReport: boolean = false) => apiCall(
    {
        url: lastReport ? queryKeyes.vessel.LastVesselReport.url(VesselID) : queryKeyes.vessel.previousReport.url(VesselID, ReportID),
        onStart: reportingAPIRequested.type,
        onSuccess: setPreviousReport.type,
        onError: reportingAPIRequestedFailed.type,
    }
)
export const getConfigurationLimitsAction = (VesselID: number) => apiCall(
    {
        url: `get_config_data/?vessel_id=${VesselID}`,
        onStart: reportingAPIRequested.type,
        onSuccess: setConfigurationLimits.type,
        onError: reportingAPIRequestedFailed.type,
    }
)
export const getCurrentReportFilesAction = (ReportID: number, VesselID: number) => apiCall(
    {
        url: `view_uploded_all_files/?report_id=${ReportID}&vessel_id=${VesselID}`,
        onStart: fileApiRequested.type,
        onSuccess: setCurrentReportFiles.type,
        onError: apiRequestedFailed.type,
    }
)